// src/pages/ServicePage.jsx
import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PublicAPI } from '../api/public';
import ServiceBanner from '../components/ServiceBanner';
import ServiceLeadForm from '../components/ServiceLeadForm';
import { normalizeMediaUrl } from '../api/base';

// Импортируем существующие страницы услуг
import SellOrRentPage from './services/SellOrRentPage';

const Container = styled.div`
  .container { 
    width: min(1400px, 96vw); 
    margin: 0 auto; 
    padding: 0 18px; 
  }
`;

const Content = styled.div`
  padding: 40px 0;
  
  .intro {
    font-size: 18px;
    line-height: 1.6;
    color: var(--muted);
    margin-bottom: 40px;
    width: 100%;
    
    p {
      margin-bottom: 15px;
    }
    
    h2, h3 {
      color: var(--fg);
      margin-top: 25px;
      margin-bottom: 15px;
    }
    
    ul, ol {
      margin: 15px 0;
      padding-left: 20px;
    }
    
    li {
      margin-bottom: 8px;
    }
    
    strong, b {
      color: var(--fg);
      font-weight: 600;
    }
  }
  
  .service-content {
    background: var(--card);
    border: 1px solid var(--bdr);
    border-radius: var(--radius);
    padding: 40px;
    box-shadow: var(--shadow);
    margin: 30px 0;
    
    h2 {
      color: var(--primary);
      margin-bottom: 20px;
      font-size: 28px;
    }
    
    p {
      color: var(--muted);
      line-height: 1.6;
      margin-bottom: 15px;
    }
    
    ul {
      list-style: none;
      padding: 0;
      margin: 20px 0;
      
      li {
        padding: 8px 0;
        color: var(--fg);
        position: relative;
        padding-left: 20px;
        
        &:before {
          content: '✓';
          position: absolute;
          left: 0;
          color: var(--primary);
          font-weight: bold;
        }
      }
    }
  }
  
  @media (max-width: 768px) {
    padding: 20px 0;
    
    .service-content {
      padding: 25px 20px;
    }
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: var(--muted);
`;

// Маппинг slug на компоненты для страниц со специальной структурой
// Все остальные услуги используют единый шаблон из базы данных
const SERVICE_COMPONENTS = {
  'sell-or-rent': SellOrRentPage, // Специальная структура с двумя карточками
};

export default function ServicePage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [service, setService] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const currentLang = i18n.language || 'ru';

  // Проверяем, есть ли специальный компонент для этой услуги
  const SpecialComponent = SERVICE_COMPONENTS[slug];

  React.useEffect(() => {
    // Если есть специальный компонент, не загружаем данные
    if (SpecialComponent) {
      setLoading(false); // Важно: устанавливаем loading в false
      return;
    }
    
    const loadService = async () => {
      try {
        setLoading(true);
        // Загружаем все услуги и ищем нужную по slug
        const data = await PublicAPI.services({ locale: currentLang });
        const services = data?.items || data?.services || [];
        
        // Ищем услугу по slug в ссылке
        const foundService = services.find(s => {
          if (!s.link) return false;
          const serviceSlug = s.link.split('/').pop();
          return serviceSlug === slug;
        });
        
        if (foundService) {
          setService(foundService);
        } else {
          setError('Услуга не найдена');
        }
      } catch (e) {
        console.error('Failed to load service:', e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    loadService();
  }, [slug, currentLang, SpecialComponent]);

  // Если есть специальный компонент, рендерим его
  if (SpecialComponent) {
    return <SpecialComponent />;
  }

  if (loading) {
    return (
      <Container>
        <div className="container">
          <LoadingWrapper>
            {t('common.loading', 'Загрузка...')}
          </LoadingWrapper>
        </div>
      </Container>
    );
  }

  if (error || !service) {
    // Перенаправляем на страницу всех услуг, если услуга не найдена
    return <Navigate to="/services" replace />;
  }

  const title = currentLang === 'zh' 
    ? (service.title_zh || service.title_en || service.title_ru)
    : currentLang === 'en'
    ? (service.title_en || service.title_ru)
    : (service.title_ru || service.title_en);
    
  const description = currentLang === 'zh'
    ? (service.description_zh || service.description_en || service.description_ru)
    : currentLang === 'en'
    ? (service.description_en || service.description_ru)
    : (service.description_ru || service.description_en);
    
  const shortDescription = currentLang === 'zh'
    ? (service.short_description_zh || service.short_description_en || service.short_description_ru)
    : currentLang === 'en'
    ? (service.short_description_en || service.short_description_ru)
    : (service.short_description_ru || service.short_description_en);
  
  // Получаем URL изображения - приоритет: cover_url, media.url, cover
  let bannerImage = null;
  if (service.cover_url) {
    bannerImage = normalizeMediaUrl(service.cover_url);
  } else if (service.media?.url) {
    bannerImage = normalizeMediaUrl(service.media.url);
  } else if (service.cover) {
    bannerImage = normalizeMediaUrl(service.cover);
  }
  
  // Для баннера используем анонс, если он есть, иначе первое предложение из описания
  const getBannerSubtitle = () => {
    if (shortDescription) {
      return shortDescription;
    }
    // Fallback: берем первое предложение из полного описания
    if (!description) return '';
    const match = description.match(/^[^.!?]+[.!?]/);
    if (match) {
      return match[0].trim();
    }
    return description.length > 100 ? description.substring(0, 100) + '...' : description;
  };
  
  const bannerSubtitle = getBannerSubtitle();

  return (
    <Container>
      <ServiceBanner
        title={title || t('services.title', 'Услуга')}
        subtitle={bannerSubtitle}
        image={bannerImage}
      />
      
      <div className="container">
        <Content>
          {description && (
            <div 
              className="intro"
              dangerouslySetInnerHTML={{ __html: description.replace(/\n/g, '<br/>') }}
            />
          )}

          <div className="service-content">
            <h2>{t('services.aboutService', 'О услуге')}</h2>
            <p>
              {t('services.contactUsText', 'Свяжитесь с нами для получения подробной информации об этой услуге. Наши специалисты проконсультируют вас и помогут подобрать оптимальное решение.')}
            </p>
            <ul>
              <li>{t('services.feature1', 'Профессиональная консультация')}</li>
              <li>{t('services.feature2', 'Индивидуальный подход')}</li>
              <li>{t('services.feature3', 'Прозрачные условия')}</li>
              <li>{t('services.feature4', 'Гарантия качества')}</li>
            </ul>
          </div>

          <ServiceLeadForm 
            type={slug}
            serviceTitle={title || t('services.title', 'Услуга')}
          />
        </Content>
      </div>
    </Container>
  );
}